/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

function initCompactCookieBanner() {
  if (document.getElementById('dogma-compact-cookie-banner-style')) return;

  const style = document.createElement('style');
  style.id = 'dogma-compact-cookie-banner-style';
  style.textContent = `
    @media screen and (max-width: 749px) {
      #shopify-pc__banner.shopify-pc__banner__dialog {
        inset: auto 1.2rem max(1.2rem, env(safe-area-inset-bottom)) 1.2rem !important;
        width: auto !important;
        max-width: none !important;
        max-height: min(34vh, 28rem) !important;
        padding: 1.4rem 1.4rem 1.2rem !important;
        overflow-y: auto !important;
        border-radius: 1.6rem !important;
      }

      #shopify-pc__banner .shopify-pc__banner__wrapper {
        width: 100% !important;
        max-width: none !important;
        gap: 1rem !important;
      }

      #shopify-pc__banner .shopify-pc__banner__body {
        margin: 0 !important;
      }

      #shopify-pc__banner #shopify-pc__banner__body-title {
        margin: 0 0 0.4rem !important;
        font-size: 1.4rem !important;
        line-height: 1.2 !important;
      }

      #shopify-pc__banner .shopify-pc__banner__body p {
        margin: 0 !important;
        font-size: 1.1rem !important;
        line-height: 1.35 !important;
      }

      #shopify-pc__banner .shopify-pc__banner__btns {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 0.8rem !important;
        margin-top: 1rem !important;
      }

      #shopify-pc__banner button {
        min-height: 3.6rem !important;
        margin: 0 !important;
        padding: 0.8rem 1rem !important;
        font-size: 1.2rem !important;
        line-height: 1.2 !important;
      }

      #shopify-pc__banner #shopify-pc__banner__btn-manage-prefs {
        grid-column: 1 / -1 !important;
        min-height: auto !important;
        padding: 0.2rem 0 !important;
        text-decoration: underline !important;
        background: transparent !important;
        border: 0 !important;
      }
    }
  `;
  document.head.appendChild(style);
}

function initCollectionBannerMobileBox() {
  if (document.getElementById('dogma-collection-banner-mobile-box-style')) return;

  const style = document.createElement('style');
  style.id = 'dogma-collection-banner-mobile-box-style';
  style.textContent = `
    @media screen and (max-width: 749px) {
      .banner:has(.banner__box.color-scheme-53e03196-13fc-4274-823b-afde0c363776) {
        min-height: 28rem !important;
      }

      .banner--medium:has(.banner__box.color-scheme-53e03196-13fc-4274-823b-afde0c363776) {
        min-height: 34rem !important;
      }

      .banner--large:has(.banner__box.color-scheme-53e03196-13fc-4274-823b-afde0c363776) {
        min-height: 39rem !important;
      }

      .banner:has(.banner__box.color-scheme-53e03196-13fc-4274-823b-afde0c363776) .banner__media {
        position: absolute !important;
        inset: 0 !important;
        height: 100% !important;
        width: 100% !important;
      }

      .banner:has(.banner__box.color-scheme-53e03196-13fc-4274-823b-afde0c363776) .banner__content {
        order: initial !important;
        min-height: inherit !important;
        position: relative !important;
        height: auto !important;
        z-index: 2 !important;
      }

      .banner:has(.banner__box.color-scheme-53e03196-13fc-4274-823b-afde0c363776) .banner__box.color-scheme-53e03196-13fc-4274-823b-afde0c363776 {
        background: rgb(var(--color-background)) !important;
        border: var(--text-boxes-border-width) solid rgba(var(--color-foreground), var(--text-boxes-border-opacity)) !important;
        border-radius: var(--text-boxes-radius) !important;
        box-shadow: var(--text-boxes-shadow-horizontal-offset) var(--text-boxes-shadow-vertical-offset) var(--text-boxes-shadow-blur-radius) rgba(var(--color-shadow), var(--text-boxes-shadow-opacity)) !important;
        margin-inline: auto !important;
        max-width: 52rem !important;
        min-width: 0 !important;
        padding: 3rem 2.4rem !important;
        width: calc(100% - 6rem) !important;
      }

      .banner:has(.banner__box.color-scheme-53e03196-13fc-4274-823b-afde0c363776) .banner__box.content-container::after {
        border-radius: var(--text-boxes-radius) !important;
      }
    }
  `;
  document.head.appendChild(style);
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

initCompactCookieBanner();
initCollectionBannerMobileBox();
